import React from "react";
import SoftBox from "../SoftBox";
import SoftTypography from "../SoftTypography";
import PageHeaderBg from "../../img/gamesettings/settings_goal_pursuit_plaza_1920.png";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";

/**
 * Page Header Component
 * @param {*} headerText main title
 * @param {*} subheaderText sub-title
 * @param {*} onBack optional — when provided, shows a mobile-only back chevron
 *   above the title (mirrors the chevron DashboardNavbar/Breadcrumbs shows on
 *   desktop, which is hidden on mobile). Opt-in: pages that don't pass it are
 *   unaffected.
 * @returns
 */
const PageHeader = ({ headerText, subheaderText, tabs, isMobile, onBack, inlineBack = false }) => {
  const subtitleLineCount = subheaderText?.split('<br/>').length || 0;

  return (
    <SoftBox
      sx={{
        position: "relative",
        marginX: { mini: "1rem", md: "1.5rem" },
        marginTop: { mini: "16px", md: "24px" },
        marginBottom: { mini: 0, md: "1.5rem" },
        borderRadius: "8px",
        overflow: { mini: "hidden", md: "visible" },
      }}
    >
      {/** Header background graphic — absolutely filled behind the content
          instead of a separately height-tracked sibling, so it always
          matches whatever height the content below needs (a fixed-height
          box here could only ever match the content by coincidence — e.g.
          the tabs toggle overflowing/clipping past the bottom edge once its
          height didn't line up with a hand-picked number). */}
      <SoftBox
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          borderRadius: "8px",
          background: `url(${PageHeaderBg})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center 54%",
          filter: "brightness(115%) opacity(0.8)",
        }}
      />

      {/** Header and subtitle text — normal flow, so the banner grows to
          fit it. Short content (just a title) is centered inside a
          comfortable minimum height; taller content (title + a tabs
          toggle) simply grows the banner past that minimum instead of
          being squeezed or clipped. */}
      <SoftBox
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: { mini: "88px", md: subtitleLineCount > 1 ? "125px" : "100px" },
          // Title-only headers (Scorekeeper, Widgets, ...) get their top/
          // bottom cushion for free from vertical centering inside
          // `minHeight`. Headers with a `tabs` toggle (Feed, Hub, Claims)
          // grow taller than that floor once the toggle is added below the
          // title, which cancels the centering and left them flush against
          // the top of the card — add the same top/bottom cushion
          // explicitly here so they read the same as the title-only ones.
          padding: { mini: "20px 16px", md: tabs ? "20px 32px" : "0 32px" },
        }}
      >
        <SoftBox>
          {/* Mobile-only back chevron — opt-in via `onBack`. Desktop already
              has one in the navbar breadcrumbs, so this stays hidden there. */}
          {onBack && !inlineBack && (
            <SoftBox
              component="button"
              type="button"
              onClick={onBack}
              aria-label="Go back"
              sx={{
                display: { mini: "flex", md: "none" },
                alignItems: "center",
                justifyContent: "center",
                width: "26px",
                height: "26px",
                marginBottom: "6px",
                marginLeft: "-4px",
                borderRadius: "50%",
                border: 0,
                background: "rgba(255,255,255,0.55)",
                cursor: "pointer",
              }}
            >
              <KeyboardArrowLeftRoundedIcon sx={{ fontSize: "1.35rem", color: "#343A40" }} />
            </SoftBox>
          )}
          {/* Figma type scale: Display xs Semibold (24/32, ls 0) on desktop,
              Text lg Semibold (18/28) on mobile. */}
          <SoftBox sx={{ display: "flex", alignItems: "center" }}>
            {onBack && inlineBack && (
              <SoftBox
                component="button"
                type="button"
                onClick={onBack}
                aria-label="Go back"
                sx={{
                  display: { mini: "flex", md: "none" },
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  padding: 0,
                  marginLeft: "-8px",
                  marginRight: "4px",
                  borderRadius: "50%",
                  border: 0,
                  color: "#343A40",
                  background: "rgba(255,255,255,0.55)",
                  cursor: "pointer",
                }}
              >
                <KeyboardArrowLeftRoundedIcon sx={{ fontSize: "1.5rem" }} />
              </SoftBox>
            )}
            <SoftTypography
              sx={{
                color: "#343A40",
                fontFamily: "DM Sans",
                fontStyle: "normal",
                fontWeight: 600,
                letterSpacing: 0,
                lineHeight: { mini: "28px", md: "32px" },
                fontSize: { mini: "18px", md: "24px" },
              }}
            >
              {headerText}
            </SoftTypography>
          </SoftBox>
          {/* Subtitle hidden on mobile to keep the banner compact */}
          {/* Figma type scale: Text sm Regular (14/20, ls 0). */}
          <SoftTypography
            sx={{
              display: { mini: "none", md: "block" },
              marginTop: "0.25rem",
              fontFamily: "DM Sans",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "14px",
              letterSpacing: 0,
              color: "#6C757D",
              lineHeight: "20px",
            }}
          >
            {subheaderText?.split('<br/>').map((line) => (
              <React.Fragment key={line}>
                {line}
                <br />
              </React.Fragment>
            )).slice(0, -1).concat(
              subheaderText?.split('<br/>').slice(-1).map((line) => (
                <React.Fragment key={`last-${line}`}>
                  {line}
                </React.Fragment>
              ))
            )}
          </SoftTypography>
        </SoftBox>

        {/* Optional tabs/toggle, rendered inside the banner below the title
            on all breakpoints. Present only for pages that pass a `tabs`
            node. */}
        {tabs && (
          <SoftBox sx={{ mt: "10px" }}>
            {tabs}
          </SoftBox>
        )}
      </SoftBox>
    </SoftBox>
  );
};

export default PageHeader;
